import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject, ChangeDetectorRef} from '@angular/core';
import {DropDownListModule} from "@syncfusion/ej2-angular-dropdowns";
import {FormInputComponent} from "../form-input/form-input.component";
import {TranslatePipe} from "../../pipes/translate.pipe";
import {FormLabelComponent} from "../form-label/form-label.component";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {TYPE} from "../../types/types";
import {forkJoin, of, Subscription} from "rxjs";
import {ApiService} from "../../services/api.service";
import {LoadingService} from "../../services/loading.service";
import {FormDropdownComponent} from "../form-dropdown/form-dropdown.component";
import {LanguageService} from "../../services/language.service";
import {IconBadgeComponent} from "../icon-badge/icon-badge.component";
import {LinkComponent} from "../link/link.component";

import {FormCheckboxComponent} from "../form-checkbox/form-checkbox.component";
import {Dialog, TooltipModule} from "@syncfusion/ej2-angular-popups";
import {DialogUtility} from "@syncfusion/ej2-popups";
import {FormButtonComponent} from "../form-button/form-button.component";
import {TooltipComponent} from "../tooltip/tooltip.component";
import {HeadlineComponent} from "../headline/headline.component";
import {ContentComponent} from "../content/content.component";
import {AvatarComponent} from "../avatar/avatar.component";
import {GroupMinimalResource} from '../../types/user/groupMinimalResource';
import {UserMinimalResource} from '../../types/knowledgebase-types/userMinimalResource';
import {Router} from '@angular/router';
import {CustomerActionType} from '../../types/user/customerActionType';

@Component({
    selector: 'app-form-user-group-mail',
    imports: [
        DropDownListModule,
        FormInputComponent,
        TranslatePipe,
        FormLabelComponent,
        ReactiveFormsModule,
        FormDropdownComponent,
        IconBadgeComponent,
        LinkComponent,
        FormCheckboxComponent,
        TooltipModule,
        FormButtonComponent,
        TooltipComponent,
        HeadlineComponent,
        ContentComponent,
        AvatarComponent
    ],
    templateUrl: './form-user-group-mail.component.html',
    styleUrl: './form-user-group-mail.component.scss'
})
export class FormUserGroupMailComponent implements OnInit, OnDestroy {
  private formBuilder: FormBuilder = inject(FormBuilder);
  private apiService: ApiService = inject(ApiService);
  private loadingService: LoadingService = inject(LoadingService);
  private languageService: LanguageService = inject(LanguageService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private router: Router = inject(Router);


  @Input() public user: number[] = [];
  @Input() public groups: number[] = [];
  @Input() public adminUserIDs: number[] = [];
  @Input() public type: 'own' | 'all' | 'active' | 'inactive' | 'registration' = 'own'; // TODO: too many types, should be simplified
  @Input() public method: 'user' | 'group' | 'userGroup' = 'userGroup';
  @Input() public allowEmails: boolean = false;
  @Input() public allowPush: boolean = false;
  @Input() public allowAdmins: boolean = false;
  @Input() public isLimitedByAllowedIDs: boolean = false;
  @Input() public allowedUserIDs: number[] = [];
  @Input() public allowedGroupIDs: number[] = [];
  @Input() public readonly: boolean = false;
  @Input() public hasError: boolean = false;
  @Input() public hasErrorField: boolean = true;
  @Input() public singleSelection: boolean = false;
  @Input() public singleSelectionStays: boolean = false;
  @Input() public can: CustomerActionType | '' = '';
  @Output() public userChange: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Output() public groupsChange: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Output() public mailsChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() public pushUserIDsChange: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Output() public pushGroupIDsChange: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Output() public adminUserIDsChange: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Output() public adminGroupIDsChange: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Output() public userNamesChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() public groupNamesChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() public userModelChange: EventEmitter<TYPE.UserListItem[]> = new EventEmitter<TYPE.UserListItem[]>();
  @Output() public groupModelChange: EventEmitter<TYPE.GroupListItem[]> = new EventEmitter<TYPE.GroupListItem[]>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public userGroupOptions: any[] = [];
  public userGroupMailForm: FormGroup = this.formBuilder.group({
    userGroup: [''],
    addMail: ['', [Validators.email]],
    userGroupMailFilter: ['all']
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public userGroupMailFilter: any[] = [];
  public participants: (TYPE.FieldSettingsModelWithId)[] = [];
  private allAllowedOptions: TYPE.FieldSettingsModelWithId[] = [];
  private subscriptions: Subscription[] = [];
  private page: number = 0;
  private searchString: string = '';
  private currentLoadingType: 'user' | 'group' = 'group';
  private dialogInstance: Dialog | null = null;
  private loadForbidden: boolean = false;
  private allowedOptionsLoaded: boolean = false;

  public ngOnInit(): void {
    if (this.method === 'user') {
      this.currentLoadingType = 'user';
    }

    if (this.allowEmails) {
      this.userGroupMailFilter = [
        {id: 'all', name: this.languageService.getTranslationByKey('SHOWALL')},
        {id: 'mail', name: this.languageService.getTranslationByKey('SHOWMAILONLY')},
        {id: 'group', name: this.languageService.getTranslationByKey('SHOWGROUPONLY')},
        {id: 'user', name: this.languageService.getTranslationByKey('SHOWUSERONLY')}
      ];
    } else {
      this.userGroupMailFilter = [
        {id: 'all', name: this.languageService.getTranslationByKey('SHOWALL')},
        {id: 'group', name: this.languageService.getTranslationByKey('SHOWGROUPONLY')},
        {id: 'user', name: this.languageService.getTranslationByKey('SHOWUSERONLY')}
      ];
    }

    this.load();
    this.loadInitials();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  private load(): void {
    if (this.loadForbidden) {
      return;
    }

    if (this.isLimitedByAllowedIDs) {
      this.loadAllowedOptions();
      return;
    }

    if (this.singleSelectionStays) {
      this.ensureSingleSelectionPlaceholder();
    }

    if (this.currentLoadingType === 'group') {
      this.loadGroups();
    } else {
      if (this.method === 'user' || this.method === 'userGroup') {
        this.loadUser();
      }
    }
  }

  private loadGroups(): void {
    this.subscriptions.push(this.apiService.getGroups(this.page, this.searchString, true, this.type, this.getCanFilter()).subscribe((groupListItems: GroupMinimalResource[]) => {

      const allowedGroupIDsSet = new Set(this.allowedGroupIDs);
      const groupListItemsTmp = (this.isLimitedByAllowedIDs ? groupListItems.filter(group => allowedGroupIDsSet.has(group.id)) : groupListItems).map(group => ({
        id: group.id,
        description: group.description,
        count: group.user_count,
        image: group.avatar?.path ?? '',
        name: group.name,
        type: 'group'
      }));

      // Create new array reference for change detection
      this.userGroupOptions = [...this.userGroupOptions, ...groupListItemsTmp];

      if (!groupListItemsTmp.length) {
        this.currentLoadingType = 'user';
        this.page = 0;
        this.load();
      }

      this.removeExistingParticipants();
      // Trigger change detection for OnPush parent components
      this.cdr.markForCheck();
    }));
  }

  private loadUser(): void {
    this.subscriptions.push(this.apiService.getUsers(this.page, this.searchString, this.type, this.getCanFilter()).subscribe((userListItems: UserMinimalResource[]) => {

      const allowedUserIDsSet = new Set(this.allowedUserIDs);
      const userListItemsTmp = (this.isLimitedByAllowedIDs ? userListItems.filter(user => allowedUserIDsSet.has(user.id)) : userListItems).map(user => ({
        id: user.id,
        image: user.avatar?.path ?? '',
        name: user.last_name + ', ' + user.first_name,
        type: 'user'
      }));

      // Create new array reference for change detection
      this.userGroupOptions = [...this.userGroupOptions, ...userListItemsTmp];

      if (!userListItemsTmp.length) {
        this.loadForbidden = true;
      }

      this.removeExistingParticipants();
      // Trigger change detection for OnPush parent components
      this.cdr.markForCheck();
    }));
  }

  private getCanFilter(): CustomerActionType | '' {
    if (this.can) {
      return this.can;
    }

    if (this.router.url.startsWith('/chat')) {
      return CustomerActionType.Chat;
    }
    if (this.router.url.startsWith('/calendar')) {
      return CustomerActionType.Calendar;
    }
    if (this.router.url.startsWith('/knowledgebase')) {
      return CustomerActionType.Knowledgebase;
    }
    if (this.router.url.startsWith('/news')) {
      return CustomerActionType.News;
    }

    return '';
  }

  private loadAllowedOptions(): void {
    if (this.allowedOptionsLoaded) {
      this.userGroupOptions = [...this.allAllowedOptions];
      this.ensureSingleSelectionPlaceholder();
      this.removeExistingParticipants();
      return;
    }

    const groupRequests = this.method !== 'user'
      ? this.allowedGroupIDs.map((groupID) => this.apiService.getGroup(groupID))
      : [];
    const userRequests = this.method !== 'group'
      ? this.allowedUserIDs.map((userID) => this.apiService.getUser(userID))
      : [];

    const groups$ = groupRequests.length ? forkJoin(groupRequests) : of([]);
    const users$ = userRequests.length ? forkJoin(userRequests) : of([]);

    this.subscriptions.push(forkJoin({groups: groups$, users: users$}).subscribe(({groups, users}) => {
      const groupOptions = (groups as TYPE.GroupDetail[]).map((group) => ({
        id: group.id,
        description: group.description,
        count: group.users?.length ?? 0,
        image: group.image,
        name: group.name,
        type: 'group'
      }));

      const userOptions = (users as TYPE.UserDetail[]).map((user) => ({
        id: user.id,
        image: user.avatar,
        name: user.last_name + ', ' + user.first_name,
        type: 'user'
      }));

      this.allAllowedOptions = [...groupOptions, ...userOptions];
      this.userGroupOptions = [...this.allAllowedOptions];
      this.allowedOptionsLoaded = true;
      this.loadForbidden = true;
      this.ensureSingleSelectionPlaceholder();
      this.removeExistingParticipants();
      // Trigger change detection for OnPush parent components
      this.cdr.markForCheck();
    }));
  }

  private ensureSingleSelectionPlaceholder(): void {
    if (!this.singleSelectionStays) {
      return;
    }

    const hasPlaceholder = this.userGroupOptions.some((item) => item.id === 0 && item.name === '' && !item.type);
    if (!hasPlaceholder) {
      // Create new array reference instead of mutating
      this.userGroupOptions = [{
        id: 0,
        description: '',
        count: 0,
        image: '',
        name: ''
      }, ...this.userGroupOptions];
    }
  }

  private removeExistingParticipants(): void {
    // Create new array reference for change detection
    this.userGroupOptions = [...this.userGroupOptions.filter((item) => {
      return !this.participants.find((participant) => participant.id === item.id && participant.type === item.type);
    })];
  }

  public loadNextPage(): void {
    if (!this.loadingService.getLoadingIndicator()) {
      if (this.isLimitedByAllowedIDs) {
        return;
      }
      this.page++;
      this.load();
    }
  }

  public search(searchString: string): void {
    if (!this.loadingService.getLoadingIndicator()) {
      if (this.isLimitedByAllowedIDs) {
        this.searchString = searchString;
        // Create new array reference for change detection
        this.userGroupOptions = [...this.filterAllowedOptions(searchString)];
        this.ensureSingleSelectionPlaceholder();
        this.removeExistingParticipants();
        // Trigger change detection for OnPush parent components
        this.cdr.markForCheck();
        return;
      }
      if (this.method !== 'user') {
        this.currentLoadingType = 'group';
      }
      this.searchString = searchString;
      this.page = 0;
      // Create new array reference for change detection
      this.userGroupOptions = [];
      this.loadForbidden = false;
      this.load();
    }
  }

  private filterAllowedOptions(searchString: string): TYPE.FieldSettingsModelWithId[] {
    const normalized = searchString.trim().toLowerCase();
    if (!normalized) {
      return [...this.allAllowedOptions];
    }

    return this.allAllowedOptions.filter((item) => item.name?.toLowerCase().includes(normalized));
  }

  private refreshAvailableOptions(): void {
    // Create new array reference for change detection
    this.userGroupOptions = [...this.filterAllowedOptions(this.searchString)];
    this.ensureSingleSelectionPlaceholder();
    this.removeExistingParticipants();
    // Trigger change detection for OnPush parent components
    this.cdr.markForCheck();
  }

  public addUserGroup(userOrGroup: string | number | TYPE.FieldSettingsModelWithId): void {
    if (typeof userOrGroup === 'string' || typeof userOrGroup === 'number') {
      userOrGroup = this.userGroupOptions.find((item) => item.id === userOrGroup) as TYPE.FieldSettingsModelWithId;
    }
    if (this.participants.find((item) => item.id === userOrGroup.id && item.type === userOrGroup.type)) {
      // already added
    } else {
      this.participants.push(userOrGroup);
      this.userGroupMailForm.addControl(userOrGroup.type! + userOrGroup.id, this.formBuilder.control(false));
    }
    setTimeout(() => {
      if (!this.singleSelectionStays) {
        this.userGroupMailForm.controls['userGroup'].setValue(null);
        this.removeExistingParticipants();
      }
      this.updateEmitters();
      // Trigger change detection for OnPush parent components
      this.cdr.markForCheck();
    }, 500);
  }

  public addMail(): void {
    if (this.userGroupMailForm.controls['addMail'].value && this.userGroupMailForm.controls['addMail'].valid && !this.participants.find((item) => item.id === this.userGroupMailForm.controls['addMail'].value && item.type === 'mail')) {
      this.participants.push({
        id: this.userGroupMailForm.controls['addMail'].value,
        name: this.userGroupMailForm.controls['addMail'].value,
        type: 'mail'
      });
      this.userGroupMailForm.addControl('mail' + this.userGroupMailForm.controls['addMail'].value, this.formBuilder.control(false));
      this.userGroupMailForm.controls['addMail'].setValue('');
      this.updateEmitters();
    }
  }

  public selectAll(): void {
    this.participants.forEach((item) => {
      this.userGroupMailForm.controls[item.type! + item.id].setValue(true);
    });
  }

  public removeSelected(): void {
    this.participants.forEach((item) => {
      if (this.userGroupMailForm.controls[item.type! + item.id].value) {
        this.userGroupMailForm.removeControl(item.type! + item.id);
      }
    });
    this.participants = this.participants.filter((item) => this.userGroupMailForm.controls[item.type! + item.id]);
    if (this.isLimitedByAllowedIDs) {
      this.refreshAvailableOptions();
    }
    this.updateEmitters();
  }

  public showDeleteOverlay(item: TYPE.FieldSettingsModelWithId): void {
    this.dialogInstance = DialogUtility.confirm({
      title: '<h6 class="text-danger fw-bold m-0 pt-1">' + this.languageService.getTranslationByKey('CONFIRMITEMDELETE') + '</h6>',
      content: this.languageService.getTranslationByKey('AREYOUSUREYOUWANTTODELETE'),
      cancelButton: {
        text: this.languageService.getTranslationByKey('CANCEL'),
        click: () => this.showDeleteOverlayCancel()
      },
      okButton: {
        text: this.languageService.getTranslationByKey('DELETE'),
        click: () => this.showDeleteOverlayConfirmed(item),
        cssClass: 'bg-danger brd-danger'
      },
      showCloseIcon: true,
      closeOnEscape: true,
      position: {X: 'center', Y: 'center'},
      animationSettings: {effect: 'Fade'},
      zIndex: 9999
    });
  }

  private showDeleteOverlayConfirmed(item: TYPE.FieldSettingsModelWithId): void {
    this.userGroupMailForm.removeControl(item.type! + item.id);
    this.participants = this.participants.filter((participant) => participant.id !== item.id || participant.type !== item.type);
    this.dialogInstance?.hide();
    
    if (this.isLimitedByAllowedIDs) {
      this.refreshAvailableOptions();
    } else {
      // Add the deleted item back to userGroupOptions if it's not a mail
      if (item.type !== 'mail' && !this.singleSelectionStays) {
        // Create new array reference instead of mutating
        this.userGroupOptions = [...this.userGroupOptions, item];
      }
    }
    
    this.userGroupMailForm.controls['userGroup'].setValue(null);
    this.updateEmitters();
    // Trigger change detection for OnPush parent components
    this.cdr.markForCheck();
  }

  private showDeleteOverlayCancel(): void {
    this.dialogInstance?.hide();
  }

  private loadInitials(): void {
    if (this.user && this.user.length) {
      this.user.forEach((userID) => {
        this.subscriptions.push(this.apiService.getUser(userID).subscribe((user) => {
          if (user) {
            this.participants.push({
              id: user.id,
              image: user.avatar,
              name: user.last_name + ', ' + user.first_name,
              type: 'user',
              admin: this.adminUserIDs.includes(user.id)
            });
            this.userGroupMailForm.addControl('user' + user.id, this.formBuilder.control(false));
          }
          this.removeExistingParticipants();
        }));
      });
    }

    if (this.groups && this.groups.length) {
      this.groups.forEach((groupID) => {
        this.subscriptions.push(this.apiService.getGroup(groupID).subscribe((group) => {
          if (group) {
            this.participants.push({
              id: group.id,
              description: group.description,
              count: group.users.length,
              image: group.image,
              name: group.name,
              type: 'group'
            });
            this.userGroupMailForm.addControl('group' + group.id, this.formBuilder.control(false));
          }
          this.removeExistingParticipants();
        }));
      });
    }
  }

  private updateEmitters(): void {
    this.userChange.emit(this.participants.filter((item) => item.type === 'user').map((item) => item.id as number));
    this.groupsChange.emit(this.participants.filter((item) => item.type === 'group').map((item) => item.id as number));
    this.mailsChange.emit(this.participants.filter((item) => item.type === 'mail').map((item) => item.id as string));
    this.pushUserIDsChange.emit(this.participants.filter((item) => item.type === 'user' && item.push).map((item) => item.id as number));
    this.pushGroupIDsChange.emit(this.participants.filter((item) => item.type === 'group' && item.push).map((item) => item.id as number));
    this.adminUserIDsChange.emit(this.participants.filter((item) => item.type === 'user' && item.admin).map((item) => item.id as number));
    this.adminGroupIDsChange.emit(this.participants.filter((item) => item.type === 'group' && item.admin).map((item) => item.id as number));
    this.userNamesChange.emit(this.participants.filter((item) => item.type === 'user').map((item) => item.name as string));
    this.groupNamesChange.emit(this.participants.filter((item) => item.type === 'group').map((item) => item.name as string));
    this.userModelChange.emit(this.participants.filter((item) => item.type === 'user').map(this.mapFieldSettingsToUserListItem));
    this.groupModelChange.emit(this.participants.filter((item) => item.type === 'group').map(this.mapFieldSettingsToGroupListItem));
  }

  public togglePush(participant: TYPE.FieldSettingsModelWithId): void {
    participant.push = !participant.push;
    this.pushUserIDsChange.emit(this.participants.filter((item) => item.type === 'user' && item.push).map((item) => item.id as number));
    this.pushGroupIDsChange.emit(this.participants.filter((item) => item.type === 'group' && item.push).map((item) => item.id as number));
    this.userModelChange.emit(this.participants.filter((item) => item.type === 'user').map(this.mapFieldSettingsToUserListItem));
    this.groupModelChange.emit(this.participants.filter((item) => item.type === 'group').map(this.mapFieldSettingsToGroupListItem));
  }

  public toggleAdmin(participant: TYPE.FieldSettingsModelWithId): void {
    participant.admin = !participant.admin;
    this.adminUserIDsChange.emit(this.participants.filter((item) => item.type === 'user' && item.admin).map((item) => item.id as number));
    this.adminGroupIDsChange.emit(this.participants.filter((item) => item.type === 'group' && item.admin).map((item) => item.id as number));
    this.userModelChange.emit(this.participants.filter((item) => item.type === 'user').map(this.mapFieldSettingsToUserListItem));
    this.groupModelChange.emit(this.participants.filter((item) => item.type === 'group').map(this.mapFieldSettingsToGroupListItem));
  }

  public mapFieldSettingsToUserListItem(field: TYPE.FieldSettingsModelWithId): TYPE.UserListItem {
    return {
      id: typeof field.id === 'string' ? parseInt(field.id, 10) : field.id,
      avatar: field.image ?? '', // falls image null ist, leeres Bild
      first_name: field.name?.split(' ')[1].replace(',', '') ?? '', // Annahme: Name enthält Vor- und Nachname
      last_name: field.name?.split(' ')[0].replace(',', '') ?? '', // fallback falls name nicht gesetzt ist
      admin: field.admin ?? false
    };
  }
  public mapFieldSettingsToGroupListItem(field: TYPE.FieldSettingsModelWithId): TYPE.GroupListItem {
    return {
      id: typeof field.id === 'string' ? parseInt(field.id, 10) : field.id,
      name: field.name ?? '',
      description: field.description ?? '',
      image: field.image ?? '',
      count: field.count ?? 0
    };
  }
}
